import { createFeatureSelector, createSelector } from '@ngrx/store';
import { portfolioFeatureKey, State } from './portfolio.reducer';
import { PortfolioComparator } from '~/shared/helpers/portfolio-comparator';
import { selectCurrentClient, selectUserProfile } from '@qvs/core/user';
import {
  CashAccount,
  EntityId,
  LoadingStatus,
  PerformanceEnum,
  PerformancePeriod,
  PortfolioNumber,
  Transaction,
  UserRoleEnum
} from '@qvs/core';
import { selectCurrentInstrumentId, selectInstrumentEntities } from '~/modules/instrument/slice';
import { PortfolioMapper } from '~/modules/portfolio/portfolio.mapper';
import { DateUtils } from '~/shared/utils/date.utils';
import { AssetHelper } from '~/shared/helpers/asset-helper';
import { environment } from '#environment';

export const selectPortfolioFeature = createFeatureSelector<State>(portfolioFeatureKey);

export const selectPortfolioFavoriteNrs = createSelector(selectPortfolioFeature, (state) => state.favoriteNumbers || []);

export const selectCreditCardPortfolioIds = createSelector(selectPortfolioFeature, (state) =>
  state ? state.creditCardPortfolioIds : []
);

export const selectPortfolioCurrentPortfolioNumber = createSelector(selectPortfolioFeature, (state) =>
  state ? state.currentNumber : null
);

export const selectPortfolioCurrentCashAccountIds = createSelector(selectPortfolioFeature, (state) =>
  state ? state.currentCashAccountIds : []
);

export const selectPortfolios = createSelector(
  selectPortfolioFeature,
  selectCurrentClient,
  selectUserProfile,
  (state, client, userProfile) =>
    (state.portfolios ?? [])
      .filter(
        (portfolio) =>
          portfolio &&
          userProfile &&
          (userProfile.role === UserRoleEnum.CLIENT || (client && portfolio.relatedUserIds?.includes(client.staffId)))
      )
      .sort((a, b) => PortfolioComparator.portfolioComparatorByName(a, b))
);

export const selectPortfoliosOrderedByFavorite = createSelector(
  selectPortfolios,
  selectPortfolioFavoriteNrs,
  (portfolios, favoriteNumbers) => {
    if (!favoriteNumbers?.length) {
      return portfolios;
    }
    const favoritePortfolios = portfolios
      .filter((portfolio) => favoriteNumbers.includes(portfolio.portfolioNumber))
      .sort((a, b) => PortfolioComparator.portfolioComparatorByName(a, b));

    const nonFavoritePortfolios = portfolios
      .filter((portfolio) => !favoriteNumbers.includes(portfolio.portfolioNumber))
      .sort((a, b) => PortfolioComparator.portfolioComparatorByName(a, b));

    return [...favoritePortfolios, ...nonFavoritePortfolios];
  }
);

export const selectPortfoliosLoadingStatus = createSelector(selectPortfolioFeature, (state) => state.portfoliosLoadingStatus);

export const selectPortfolioLoadingStatus = createSelector(selectPortfolioFeature, (state) => state.portfolioLoadingStatus);

export const selectPortfolioIsLoading = createSelector(
  selectPortfoliosLoadingStatus,
  selectPortfolioLoadingStatus,
  (portfoliosStatus, portfolioStatus) => portfoliosStatus === LoadingStatus.LOADING || portfolioStatus === LoadingStatus.LOADING
);

export const selectPerformanceIsLoading = createSelector(
  selectPortfolioFeature,
  (state) =>
    state.performanceLoadingStatus.currentYear === LoadingStatus.LOADING ||
    state.performanceLoadingStatus.previousYear === LoadingStatus.LOADING ||
    state.performanceLoadingStatus.inception === LoadingStatus.LOADING
);

export const selectPortfolioCurrent = createSelector(
  selectPortfolioCurrentPortfolioNumber,
  selectPortfolios,
  selectPortfolioFavoriteNrs,
  (currentNumber, portfolios, favoriteNrs) =>
    portfolios
      ? portfolios.find((portfolio) => portfolio.portfolioNumber === currentNumber) ||
        portfolios.find((portfolio) => favoriteNrs.includes(portfolio.portfolioNumber)) ||
        portfolios.find(() => true) ||
        null
      : null
);

export const selectPortfolioCurrentNumber = createSelector(selectPortfolioCurrent, (portfolio) =>
  portfolio ? portfolio.portfolioNumber : null
);

export const selectCurrentPortfolioCashAccounts = createSelector(
  selectPortfolioCurrent,
  (portfolio) => AssetHelper.getCashAllocation(portfolio?.assetAllocations)?.accounts ?? []
);

export const selectCurrentPortfolioCashTransactionsOrderedByDateDesc = createSelector(
  selectCurrentPortfolioCashAccounts,
  (cashAccounts) => {
    const transactions: Transaction[] = [];
    cashAccounts.forEach((account: CashAccount) => {
      account.transactions.forEach((transaction) => transactions.push(transaction));
    });
    return transactions.sort((a, b) => DateUtils.new(b.date).diff(a.date));
  }
);

export const selectCurrentPortfolioLastTransactions = createSelector(
  selectCurrentPortfolioCashTransactionsOrderedByDateDesc,
  (transactions) => transactions.slice(0, 4)
);

export const isPortfolioCashAccountsSelectedAll = createSelector(
  selectPortfolioCurrentCashAccountIds,
  (accountIds) => !accountIds || accountIds.length < 1
);

export const selectPortfolioTransactionsIsLoading = createSelector(
  selectCurrentPortfolioCashAccounts,
  (accounts: CashAccount[]) => accounts.some((account) => account.isLoading)
);

export const selectCurrentAssetAllocationInstrument = createSelector(
  selectPortfolioFeature,
  selectPortfolioCurrent,
  (state, currentPortfolio) =>
    currentPortfolio?.assetAllocations
      ?.flatMap((allocation) => allocation.instruments ?? [])
      .find((instrument) => instrument.positionId === state.currentInstrumentPositionId) ?? null
);

export const selectPortfoliosIdMap = createSelector(selectPortfolios, (state) => {
  const map = {};
  state.forEach((portfolio) => (map[portfolio.portfolioNumber] = portfolio));
  return map;
});

export const selectCurrentPortfolioInstrumentsWhichAllowTrading = createSelector(selectPortfolioCurrent, (currentPortfolio) =>
  PortfolioMapper.mapPortfolioInstruments(currentPortfolio).filter((instrument) => instrument?.isTradingAllowed)
);

export const selectCurrentPortfolioInstrumentsWithTicker = createSelector(
  selectCurrentPortfolioInstrumentsWhichAllowTrading,
  selectInstrumentEntities,
  (portfolioInstruments, allInstrumentEntities) =>
    portfolioInstruments.map((portfolioInstrument) => ({
      ...portfolioInstrument,
      ticker: allInstrumentEntities[portfolioInstrument.securityId]?.ticker,
      shortDescription: allInstrumentEntities[portfolioInstrument.securityId]?.shortDescription
    }))
);

export const selectAllPortfolioInstrumentIds = createSelector(selectPortfolios, (portfolios) =>
  portfolios
    .flatMap((portfolio) => PortfolioMapper.mapPortfolioInstruments(portfolio))
    .map((instrument) => instrument?.securityId)
);

export const selectCurrentInstrumentIsAbleToSell = createSelector(
  selectCurrentInstrumentId,
  selectAllPortfolioInstrumentIds,
  (currentInstrumentId, portfolioInstrumentIds) => portfolioInstrumentIds.includes(currentInstrumentId as number)
);

export const selectCurrentAssetAllocation = createSelector(selectPortfolioFeature, selectPortfolioCurrent, (state, portfolio) =>
  portfolio?.assetAllocations?.find((asset) => asset.id === state.currentAssetAllocationId)
);

export const selectCurrentPortfolioInstrument = createSelector(
  selectPortfolioFeature,
  selectCurrentAssetAllocation,
  (state, asset) => asset?.instruments?.find((instrument) => instrument.positionId === state.currentInstrumentPositionId)
);

export const selectPortfolioById = (portfolioNumber: PortfolioNumber) =>
  createSelector(selectPortfolios, (portfolios) => portfolios.find((portfolio) => portfolio.portfolioNumber === portfolioNumber));

export const selectIsInstrumentAbleToSellById = (securityId: EntityId) =>
  createSelector(selectAllPortfolioInstrumentIds, (instruments) => instruments.includes(securityId as number));

export const selectPortfolioFromAccount = (accountId: EntityId) =>
  createSelector(selectPortfolios, (portfolios) =>
    portfolios.find((portfolio) =>
      AssetHelper.getCashAllocation(portfolio.assetAllocations)?.accounts?.some(
        (account: CashAccount) => account.id === accountId
      )
    )
  );

export const selectCurrentPortfolioPerformances = createSelector(selectPortfolioCurrent, (portfolio) => portfolio?.performance);

export const selectCurrentYearPerformance = createSelector(
  selectCurrentPortfolioPerformances,
  (performance) => performance?.currentYearPerformance
);

export const selectPreviousYearPerformance = createSelector(
  selectCurrentPortfolioPerformances,
  (performance) => performance?.previousYearPerformance
);

export const selectInceptionPerformance = createSelector(
  selectCurrentPortfolioPerformances,
  (performance) => performance?.inceptionPerformance
);

export const selectDepositaryBanks = createSelector(selectPortfolioFeature, (state) =>
  environment.featureFlags.isDepositaryBanksEnabled ? state.depositaryBanks : []
);

export const selectDepositaryBankNameById = (id: string) =>
  createSelector(
    selectDepositaryBanks,
    (depositaryBanks) => depositaryBanks?.find((depositaryBank) => depositaryBank.id === id)?.description
  );

export const selectHistoricalPerformance = createSelector(selectInceptionPerformance, (performance) => {
  const maxYear = DateUtils.new().year() - 3;
  const subPeriods = performance?.performanceSubPeriods?.filter((period) => period?.endDate.getFullYear() >= maxYear);

  if (!subPeriods || subPeriods.length === 0) {
    return null;
  }
  return {
    ...performance,
    beginDate: subPeriods[0].beginDate,
    endDate: subPeriods[subPeriods.length - 1].endDate,
    netBeginAmount: subPeriods[0].netBeginAmount,
    netEndAmount: subPeriods[subPeriods.length - 1].netEndAmount,
    totalFlow: subPeriods.reduce((acc, period) => acc + period.totalFlow, 0),
    performanceAmount: subPeriods.reduce((acc, period) => acc + period.performanceAmount, 0),
    performanceRate: calculatePerformanceRate(subPeriods),
    performanceSubPeriods: subPeriods
  } as PerformancePeriod;
});

export const selectPortfolioPerformanceTimeRange = createSelector(
  selectPortfolioFeature,
  (state) => state.portfolioPerformanceTimeRange
);

export const selectCurrentPortfolioPerformanceForTimeRange = createSelector(
  selectPortfolioPerformanceTimeRange,
  selectCurrentYearPerformance,
  selectPreviousYearPerformance,
  selectInceptionPerformance,
  selectHistoricalPerformance,
  (timeRange, currentYear, previousYear, inception, historical) => {
    switch (timeRange) {
      case PerformanceEnum.PREVIOUS_YEAR:
        return previousYear;
      case PerformanceEnum.INCEPTION:
        return inception;
      case PerformanceEnum.HISTORICAL:
        return historical;
      case PerformanceEnum.CURRENT_YEAR:
      default:
        return currentYear;
    }
  }
);

const calculatePerformanceRate = (subPeriods: PerformancePeriod[]) => {
  const initialValue = 1;
  const growthFactor = subPeriods.reduce((acc, period) => {
    const rate = 1 + period.performanceRate / 100;
    return acc * rate;
  }, initialValue);
  const compoundedRate = growthFactor - 1;
  return compoundedRate * 100;
};
